import { PathRequestPartStatusEnum, PathRequestPartTypeEnum, PathRequestProgressStatusEnum } from '@elearning-platform/api-types/enums';
import { PathPartInfoInterface } from '@elearning-platform/api-types/interfaces';
import { Color, IconName, StatusInfoBoxBadgeData, StatusInfoBoxVariant, TasksListData, TasksListItem } from '@elearning-platform/ui';
import { useCallback } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { ContextTranslation, UsePathRequestStatusBoxCommonConfig } from '../interfaces';

export const usePathRequestStatusBoxCommonConfig: () => UsePathRequestStatusBoxCommonConfig = (): UsePathRequestStatusBoxCommonConfig => {
  const { t }: TransProps<never> = useTranslation();

  const getPathRequestPartTypeDescriptions: (part: PathRequestPartTypeEnum) => ContextTranslation = (
    part: PathRequestPartTypeEnum
  ): ContextTranslation => {
    switch (part) {
      case PathRequestPartTypeEnum.Preform:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.preForm.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.preForm.tooltip'),
        };
      case PathRequestPartTypeEnum.DataForm:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.dataForm.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.dataForm.tooltip'),
        };
      case PathRequestPartTypeEnum.AuthorizationUpload:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.authorizationUpload.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.authorizationUpload.tooltip'),
        };
      case PathRequestPartTypeEnum.CourseMaterials:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.courseMaterials.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.courseMaterials.tooltip'),
        };
      case PathRequestPartTypeEnum.ExamRegistration:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.examRegistration.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.examRegistration.tooltip'),
        };
      case PathRequestPartTypeEnum.ExamResult:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.examResult.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.examResult.tooltip'),
        };
      case PathRequestPartTypeEnum.FileDownload:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.fileDownload.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.fileDownload.tooltip'),
        };
      case PathRequestPartTypeEnum.FileUploadForm:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.fileUploadForm.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.fileUploadForm.tooltip'),
        };
      case PathRequestPartTypeEnum.PaperDocumentFlow:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.paperDocumentFlow.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.paperDocumentFlow.tooltip'),
        };
      case PathRequestPartTypeEnum.ExamTypeAssignment:
        return {
          title: t('toyota.app.pathRequests.usePathRequests.context.examTypeAssignment.title'),
          tooltipText: t('toyota.app.pathRequests.usePathRequests.context.examTypeAssignment.tooltip'),
        };
      default:
        part satisfies never;

        return part;
    }
  };

  const getUserPathRequestPartsItems: (parts: Partial<Record<PathRequestPartTypeEnum, PathPartInfoInterface>>) => TasksListData = (
    parts: Partial<Record<PathRequestPartTypeEnum, PathPartInfoInterface>>
  ): TasksListData => ({
    label: t('toyota.app.pathRequests.userPathRequests.requestItem.done'),
    items: Object.entries(parts)
      .filter((item: [string, PathPartInfoInterface]): boolean => item[1].status !== PathRequestPartStatusEnum.Initialized)
      .map(([type, statusDetails]: [string, PathPartInfoInterface]): TasksListItem => ({
        text: getPathRequestPartTypeDescriptions(type as PathRequestPartTypeEnum).title,
        icon: (statusDetails.status === PathRequestPartStatusEnum.Finished || statusDetails.status === PathRequestPartStatusEnum.Exempted)
          ? IconName.CheckSolid
          : IconName.HourglassHalf,
      })),
  });

  const getPathRequestBoxVariant: (
    progressStatus: PathRequestProgressStatusEnum, isCurrentUserOwner: boolean
  ) => StatusInfoBoxVariant = (
    progressStatus: PathRequestProgressStatusEnum, isCurrentUserOwner: boolean
  ): StatusInfoBoxVariant => {
    switch (progressStatus) {
      case PathRequestProgressStatusEnum.PreFormOpen:
      case PathRequestProgressStatusEnum.PreFormInProgress:
      case PathRequestProgressStatusEnum.PreFormInRetakeProgress:
      case PathRequestProgressStatusEnum.FileDownloadPending:
      case PathRequestProgressStatusEnum.DataFormOpen:
      case PathRequestProgressStatusEnum.DataFormInProgress:
      case PathRequestProgressStatusEnum.DataFormInRetakeProgress:
      case PathRequestProgressStatusEnum.UploadFormOpen:
      case PathRequestProgressStatusEnum.UploadFormInProgress:
      case PathRequestProgressStatusEnum.UploadFormInRetakeProgress:
      case PathRequestProgressStatusEnum.ExamRegistrationOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakeOpen:
      case PathRequestProgressStatusEnum.ExamResultsOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRescheduleOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationPending:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakePending:
      case PathRequestProgressStatusEnum.CourseMaterialsOpen:
        return isCurrentUserOwner
          ? StatusInfoBoxVariant.Warning
          : StatusInfoBoxVariant.Default;
      case PathRequestProgressStatusEnum.PreFormWaitingForReview:
      case PathRequestProgressStatusEnum.PreFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.PreFormInReview:
      case PathRequestProgressStatusEnum.PreFormInRetakeReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.DataFormInReview:
      case PathRequestProgressStatusEnum.DataFormInRetakeReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.UploadFormInReview:
      case PathRequestProgressStatusEnum.UploadFormInRetakeReview:
      case PathRequestProgressStatusEnum.ExamResultsInReview:
      case PathRequestProgressStatusEnum.AuthorizationUploadOpen:
      case PathRequestProgressStatusEnum.ExamTypeAssignmentOpen:
        return isCurrentUserOwner
          ? StatusInfoBoxVariant.Default
          : StatusInfoBoxVariant.Warning;
      case PathRequestProgressStatusEnum.PreFormRejected:
      case PathRequestProgressStatusEnum.PreFormRejectedInRetake:
      case PathRequestProgressStatusEnum.DataFormRejected:
      case PathRequestProgressStatusEnum.DataFormRejectedInRetake:
      case PathRequestProgressStatusEnum.UploadFormRejected:
      case PathRequestProgressStatusEnum.UploadFormRejectedInRetake:
        return isCurrentUserOwner
          ? StatusInfoBoxVariant.Error
          : StatusInfoBoxVariant.Default;
      case PathRequestProgressStatusEnum.Finished:
        return StatusInfoBoxVariant.Success;
      case PathRequestProgressStatusEnum.SystemProcessing:
        return StatusInfoBoxVariant.Default;
      default:
        progressStatus satisfies never;

        return StatusInfoBoxVariant.Default;
    }
  };

  const getPathRequestBoxBadge: (
    progressStatus: PathRequestProgressStatusEnum, isCurrentUserOwner: boolean
  ) => StatusInfoBoxBadgeData | undefined = (
    progressStatus: PathRequestProgressStatusEnum, isCurrentUserOwner: boolean
  ): StatusInfoBoxBadgeData | undefined => {
    switch (progressStatus) {
      case PathRequestProgressStatusEnum.PreFormOpen:
      case PathRequestProgressStatusEnum.PreFormInProgress:
      case PathRequestProgressStatusEnum.PreFormInRetakeProgress:
      case PathRequestProgressStatusEnum.FileDownloadPending:
      case PathRequestProgressStatusEnum.DataFormOpen:
      case PathRequestProgressStatusEnum.DataFormInProgress:
      case PathRequestProgressStatusEnum.DataFormInRetakeProgress:
      case PathRequestProgressStatusEnum.UploadFormOpen:
      case PathRequestProgressStatusEnum.UploadFormInProgress:
      case PathRequestProgressStatusEnum.UploadFormInRetakeProgress:
      case PathRequestProgressStatusEnum.ExamRegistrationOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRescheduleOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationPending:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakeOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakePending:
      case PathRequestProgressStatusEnum.ExamResultsOpen:
      case PathRequestProgressStatusEnum.CourseMaterialsOpen:
        return isCurrentUserOwner
          ? {
              content: t('toyota.app.global.requiredAction'),
              icon: IconName.WarningOutlined,
              colorsData: { textColor: Color.Yellow900, backgroundColor: Color.Yellow300 },
            }
          : undefined;
      case PathRequestProgressStatusEnum.PreFormWaitingForReview:
      case PathRequestProgressStatusEnum.PreFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.PreFormInReview:
      case PathRequestProgressStatusEnum.PreFormInRetakeReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.DataFormInReview:
      case PathRequestProgressStatusEnum.DataFormInRetakeReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.UploadFormInReview:
      case PathRequestProgressStatusEnum.ExamTypeAssignmentOpen:
      case PathRequestProgressStatusEnum.UploadFormInRetakeReview:
      case PathRequestProgressStatusEnum.ExamResultsInReview:
      case PathRequestProgressStatusEnum.AuthorizationUploadOpen:
        return !isCurrentUserOwner
          ? {
              content: t('toyota.app.global.requiredAction'),
              icon: IconName.WarningOutlined,
              colorsData: { textColor: Color.Yellow900, backgroundColor: Color.Yellow300 },
            }
          : undefined;
      case PathRequestProgressStatusEnum.PreFormRejected:
      case PathRequestProgressStatusEnum.PreFormRejectedInRetake:
      case PathRequestProgressStatusEnum.DataFormRejected:
      case PathRequestProgressStatusEnum.DataFormRejectedInRetake:
      case PathRequestProgressStatusEnum.UploadFormRejected:
      case PathRequestProgressStatusEnum.UploadFormRejectedInRetake:
        return isCurrentUserOwner
          ? {
              content: t('toyota.app.global.requiredAction'),
              icon: IconName.WarningOutlined,
              colorsData: { textColor: Color.White, backgroundColor: Color.Red300 },
            }
          : undefined;
      case PathRequestProgressStatusEnum.Finished:
        return {
          content: t('toyota.app.usePathRequestStatusBoxCommonConfig.activeKnf'),
          icon: IconName.Done,
          colorsData: { textColor: Color.White, backgroundColor: Color.Green300 },
        };
      case PathRequestProgressStatusEnum.SystemProcessing:
        return undefined;
      default:
        progressStatus satisfies never;

        return undefined;
    }
  };

  const getPathRequestProgress: (parts: Partial<Record<PathRequestPartTypeEnum, PathPartInfoInterface>>) => number = useCallback(
    (
      parts: Partial<Record<PathRequestPartTypeEnum, PathPartInfoInterface>>
    ): number => {
      const completedPartsCount: number = Object.values(parts)
        .map((item: PathPartInfoInterface): number => {
          if (
            [
              PathRequestPartStatusEnum.WaitingForReview,
              PathRequestPartStatusEnum.WaitingForRetakeReview,
              PathRequestPartStatusEnum.InReview,
              PathRequestPartStatusEnum.InRetakeReview,
            ].includes(item.status)
          ) {
            return 0.5;
          }
          if (item.status === PathRequestPartStatusEnum.Finished) {
            return 1;
          }

          return 0;
        })
        .reduce((acc: number, current: number): number => acc + current, 0);
      const allPartsCount: number = Object.values(parts).length;

      return Math.round((completedPartsCount / allPartsCount) * 100);
    },
    []
  );

  return {
    getPathRequestPartTypeDescriptions,
    getUserPathRequestPartsItems,
    getPathRequestProgress,
    getPathRequestBoxVariant,
    getPathRequestBoxBadge,
  };
};
